import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { playersAPI, statsAPI, participationsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

function PlayerModal({ player, sports, countries, clubs, onClose, onSave }) {
  const [form, setForm] = useState(player || { gender: 'male' });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setLoading(true);
    try { await onSave(form); onClose(); }
    catch (e) { toast.error(e.response?.data?.message || 'حدث خطأ'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>{player ? 'تعديل اللاعب' : 'إضافة لاعب جديد'}</h3>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">الاسم (عربي)</label>
              <input value={form.full_name_ar || ''} onChange={e => set('full_name_ar', e.target.value)} placeholder="محمد أحمد" />
            </div>
            <div className="form-group">
              <label className="form-label">الاسم (إنجليزي)</label>
              <input value={form.full_name || ''} onChange={e => set('full_name', e.target.value)} placeholder="Mohamed Ahmed" />
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">الجنسية</label>
              <select value={form.nationality_id || ''} onChange={e => set('nationality_id', e.target.value)}>
                <option value="">اختر الدولة</option>
                {countries?.data?.map(c => <option key={c.id} value={c.id}>{c.flag_emoji} {c.name_ar || c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">الرياضة</label>
              <select value={form.sport_id || ''} onChange={e => set('sport_id', e.target.value)}>
                <option value="">اختر الرياضة</option>
                {sports?.data?.map(s => <option key={s.id} value={s.id}>{s.icon} {s.name_ar || s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">النادي</label>
              <select value={form.club_id || ''} onChange={e => set('club_id', e.target.value)}>
                <option value="">بدون نادي</option>
                {clubs?.data?.map(c => <option key={c.id} value={c.id}>{c.name_ar || c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">تاريخ الميلاد</label>
              <input type="date" value={form.date_of_birth || ''} onChange={e => set('date_of_birth', e.target.value)} />
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">الجنس</label>
              <select value={form.gender} onChange={e => set('gender', e.target.value)}>
                <option value="male">ذكر</option>
                <option value="female">أنثى</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">الفئة العمرية</label>
              <select value={form.age_category || 'men'} onChange={e => set('age_category', e.target.value)}>
                <option value="men">رجال</option>
                <option value="women">سيدات</option>
                <option value="junior_men">ناشئين</option>
                <option value="junior_women">ناشئات</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">نبذة تعريفية</label>
            <textarea rows={3} value={form.bio || ''} onChange={e => set('bio', e.target.value)} placeholder="نبذة عن اللاعب..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>إلغاء</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? '...' : (player ? 'حفظ التعديلات' : 'إضافة اللاعب')}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddParticipationModal({ player, onClose }) {
  const [form, setForm] = useState({ player_id: player.id });
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const { data: tournaments } = useQuery({ queryKey: ['tournaments-all'], queryFn: () => import('../utils/api').then(m => m.tournamentsAPI.getAll({ limit: 100 }).then(r => r.data)) });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.tournament_id) return toast.error('اختر البطولة');
    setLoading(true);
    try {
      const res = await participationsAPI.add(form);
      toast.success(res.data.message);
      queryClient.invalidateQueries(['leaderboard']);
      queryClient.invalidateQueries(['player', String(player.id)]);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'حدث خطأ');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>إضافة مشاركة - {player.full_name_ar || player.full_name}</h3>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">البطولة</label>
            <select value={form.tournament_id || ''} onChange={e => set('tournament_id', e.target.value)}>
              <option value="">اختر البطولة</option>
              {tournaments?.data?.map(t => <option key={t.id} value={t.id}>{t.name_ar || t.name} ({t.level})</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">المركز النهائي (0 = مشاركة فقط)</label>
            <input type="number" min="0" max="100" value={form.placement || ''} onChange={e => set('placement', parseInt(e.target.value))} placeholder="1 = أول, 2 = ثاني..." />
          </div>
          <div className="form-group">
            <label className="form-label">ملاحظات</label>
            <textarea rows={2} value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
          </div>
          <div className="alert alert-success" style={{ fontSize: 12 }}>
            💡 سيتم احتساب النقاط تلقائياً بناءً على مستوى البطولة والمركز
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>إلغاء</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? '...' : 'إضافة المشاركة'}
          </button>
        </div>
      </div>
    </div>
  );
}

const AGE_LABELS = { men: 'رجال', women: 'سيدات', junior_men: 'ناشئين', junior_women: 'ناشئات' };
export default function Players() {
  const navigate = useNavigate();
  const { isEditor, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ page: 1, search: '' });
  const [modal, setModal] = useState(null); // null | {type:'add'|'edit'|'participate', player?}

  const { data, isLoading } = useQuery({
    queryKey: ['players', filters],
    queryFn: () => playersAPI.getAll({ ...filters, limit: 20 }).then(r => r.data)
  });

  const { data: sports } = useQuery({ queryKey: ['sports'], queryFn: () => statsAPI.sports().then(r => r.data) });
  const { data: countries } = useQuery({ queryKey: ['countries'], queryFn: () => statsAPI.countries().then(r => r.data) });
  const { data: clubs } = useQuery({ queryKey: ['clubs'], queryFn: () => statsAPI.clubs().then(r => r.data) });

  const addMutation = useMutation({
    mutationFn: (data) => playersAPI.create(data),
    onSuccess: () => { toast.success('تم إضافة اللاعب'); queryClient.invalidateQueries(['players']); }
  });

  const editMutation = useMutation({
    mutationFn: ({ id, data }) => playersAPI.update(id, data),
    onSuccess: () => { toast.success('تم تحديث اللاعب'); queryClient.invalidateQueries(['players']); }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => playersAPI.delete(id),
    onSuccess: () => { toast.success('تم حذف اللاعب'); queryClient.invalidateQueries(['players']); }
  });

  const players = data?.data || [];

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">👤 إدارة اللاعبين</div>
        <div className="topbar-actions">
          {isEditor && (
            <button className="btn btn-primary" onClick={() => setModal({ type: 'add' })}>
              + إضافة لاعب
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        <div className="filter-bar">
          <div className="search-bar" style={{ flex: 1, maxWidth: 320 }}>
            <input placeholder="🔍 بحث..." onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} />
          </div>
          <select onChange={e => setFilters(f => ({ ...f, sport_id: e.target.value, page: 1 }))}>
            <option value="">جميع الرياضات</option>
            {sports?.data?.map(s => <option key={s.id} value={s.id}>{s.icon} {s.name_ar}</option>)}
          </select>
          <select onChange={e => setFilters(f => ({ ...f, country_id: e.target.value, page: 1 }))}>
            <option value="">جميع الدول</option>
            {countries?.data?.map(c => <option key={c.id} value={c.id}>{c.flag_emoji} {c.name_ar}</option>)}
          </select>
        </div>

        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            {isLoading ? (
              <div className="loading"><div className="spinner" /></div>
            ) : players.length === 0 ? (
              <div className="empty"><div className="empty-icon">👤</div><p>لا يوجد لاعبون</p></div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>اللاعب</th>
                    <th>الدولة</th>
                    <th>الرياضة</th>
                    <th>النادي</th>
                    <th>الترتيب</th>
                    <th>النقاط</th>
                    <th>الميداليات</th>
                    {isEditor && <th>الإجراءات</th>}
                  </tr>
                </thead>
                <tbody>
                  {players.map(p => (
                    <tr key={p.id}>
                      <td onClick={() => navigate(`/players/${p.id}`)}>
                        <div className="player-cell">
                          <div className="player-avatar">
                            {p.photo_url ? <img src={p.photo_url} alt="" /> : p.full_name.charAt(0)}
                          </div>
                          <div className="player-info">
                            <div className="name">{p.full_name_ar || p.full_name}</div>
                            {p.full_name_ar && <div className="name-ar">{p.full_name}</div>}
                          </div>
                        </div>
                      </td>
                      <td>{p.flag_emoji} {p.country_name_ar || '—'}</td>
                      <td>{p.sport_icon} {p.sport_name_ar || '—'}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{p.club_name || '—'}</td>
                      <td>{p.rank_position > 0 ? <span className="points-value" style={{ color: 'var(--gold)' }}>#{p.rank_position}</span> : '—'}</td>
                      <td><span className="points-value">{Math.round(p.total_points).toLocaleString()}</span></td>
                      <td>
                        <span style={{ color: 'var(--gold)' }}>🥇{p.gold_medals}</span>{' '}
                        <span style={{ color: 'var(--silver)' }}>🥈{p.silver_medals}</span>{' '}
                        <span style={{ color: 'var(--bronze)' }}>🥉{p.bronze_medals}</span>
                      </td>
                      {isEditor && (
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => setModal({ type: 'participate', player: p })}>+ مشاركة</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => setModal({ type: 'edit', player: p })}>تعديل</button>
                            {isAdmin && (
                              <button className="btn btn-danger btn-sm"
                                onClick={() => { if (window.confirm('حذف اللاعب؟')) deleteMutation.mutate(p.id); }}>
                                حذف
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {data?.pages > 1 && (
            <div className="pagination" style={{ padding: '16px 0' }}>
              {Array.from({ length: data.pages }, (_, i) => i + 1).map(pg => (
                <button key={pg} className={`page-btn ${pg === filters.page ? 'active' : ''}`}
                  onClick={() => setFilters(f => ({ ...f, page: pg }))}>
                  {pg}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {modal?.type === 'add' && (
        <PlayerModal sports={sports} countries={countries} clubs={clubs}
          onClose={() => setModal(null)}
          onSave={(form) => addMutation.mutateAsync(form)} />
      )}
      {modal?.type === 'edit' && (
        <PlayerModal player={modal.player} sports={sports} countries={countries} clubs={clubs}
          onClose={() => setModal(null)}
          onSave={(form) => editMutation.mutateAsync({ id: modal.player.id, data: form })} />
      )}
      {modal?.type === 'participate' && (
        <AddParticipationModal player={modal.player} onClose={() => setModal(null)} />
      )}
    </div>
  );
}
